/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../../store/hooks";
import { useActiveStep } from "./hooks";
import { step1st2nd, stepsBulkUpload } from "./AddSiteStep";
import { Sheet } from "../../../../components/ui/sheet";
import { setBasicDialogOpenFor, setErrorMsg } from "../../../../store/helpers/helperReducers";
import { selectedFileBulkSelector } from "../../../../store/api/admin/selectors";
import { ISiteOfflineDetails } from "../../../../store/api/devices/types";
import { siteOfflineSelector } from "../../../../store/api/devices/selectors";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { IKeyValue } from "../../../../components/Administrations/types";
import { setCurrentSiteGroups, setSelectedBulkFileUpload } from "../../../../store/api/admin/reducers";
import { setSiteOfflineDevice } from "../../../../store/api/devices/reducers";
import { resetSiteOfflineDetails } from "../../../../components/helpers";
import { useRegionDisplay } from "../../AccessControls/Regions/hooks";
import { IGeoRegion } from "../../../../store/api/admin/types";
import { selectedGeoRegionSelector, selectedGeoRegionIdsSelector } from "../../../../store/api/admin/selectors";
import { AppState } from "../../../../store";
import { LOCAL_STORAGE } from "../../../../globals/globals";
import { Button } from "../../../../components/ui/button";

const AddSiteModal: React.FC = () => {
  const dispatch = useStoreDispatch();
  const selectedGeoRegion = useSelector<AppState, IGeoRegion>(selectedGeoRegionSelector);
  const selectedGeoRegionIds = useSelector<AppState, string[] | undefined>(selectedGeoRegionIdsSelector);
  const selectedBulkFile = useSelector(selectedFileBulkSelector);
  const errorMsg = useSelector(errorMsgSelector);
  const { geoRegions } = useRegionDisplay();
  const siteOfflineDetails: ISiteOfflineDetails = useSelector(siteOfflineSelector);
  const { activeStep, setActiveStep, isBulkUpload, setIsBulkUpload, handleBulkUpload, handleAddOfflineSite, isLoadingStatus, setIsLoadingStatus } = useActiveStep();
  const isDas = (device: ISiteOfflineDetails) : boolean => {
    return device.siteType === "DAS";
  };
  useEffect(() => {
    if (geoRegions && geoRegions.length && geoRegions[0] != null) {
      const updatedGeoRegions = geoRegions || [];
      const allowedRegionIds = (selectedGeoRegionIds && selectedGeoRegionIds.length > 0)
        ? selectedGeoRegionIds
        : updatedGeoRegions.map((region: IGeoRegion) => region.objectKey);

      const siteGroups = updatedGeoRegions
        .filter((region: IGeoRegion) => allowedRegionIds.includes(region.objectKey))
        .flatMap((region: IGeoRegion) => region.siteGroups || []);

      dispatch(setCurrentSiteGroups(siteGroups.length ? siteGroups : []));

      const defaultRegion = updatedGeoRegions.find((region: IGeoRegion) => allowedRegionIds.includes(region.objectKey)) || selectedGeoRegion;
      if (defaultRegion && defaultRegion.objectKey) {
        const defaultRegionSiteGroups = defaultRegion.siteGroups || [];
        localStorage.setItem(LOCAL_STORAGE.CURRENTREGION, JSON.stringify({ ...defaultRegion, siteGroups: defaultRegionSiteGroups }));
      }
    }
  }, [geoRegions, selectedGeoRegionIds, selectedGeoRegion, dispatch]);

  const enableNext = (activeStep: number, formData: ISiteOfflineDetails, errorMsg: IKeyValue) => {
  let isEnable: boolean = false;
  
  if (activeStep === 0) {
    const step1Errors = ['group', 'siteType', 'siteName', 'serialNumber'];
    const hasStep1Errors = step1Errors.some(field => errorMsg[field] && errorMsg[field] !== "");
    const hasRequiredValues =
      !!formData.group?.trim() &&
      !!formData.siteType?.trim() &&
      !!formData.siteName?.trim() &&
      !!formData.serialNumber?.trim();
    isEnable = hasRequiredValues && !hasStep1Errors;
  } else if (activeStep === 1) {
    let step2Errors = ['description', 'version', 'modelNumber', 'latitude', 'longitude'];
    if(isDas(formData)){
      step2Errors = ['ipAddress', 'snmpPort', 'snmpVersion', 'timeout', 'modelNumber'];
      if(formData.snmpVersion === '1'){
        step2Errors.push('readCommunity');
        step2Errors.push('writeCommunity');
      }
      else{
        step2Errors.push('username');
        // contextName is always optional
        step2Errors.push('authProtocol');
        if (formData.authProtocol && formData.authProtocol !== 'NO_AUTH') step2Errors.push('authPassword');
        step2Errors.push('privacyProtocol');
        if (formData.privacyProtocol && formData.privacyProtocol !== 'NO_PRIV') step2Errors.push('privacyPassword');
      }
    }
    const hasStep2Errors = step2Errors.some(field => errorMsg[field] && errorMsg[field] !== "");
        
    if(isDas(formData)){
      isEnable =
      !!formData.ipAddress?.trim() &&
      !!formData.snmpVersion?.trim() &&
      !!formData.modelNumber?.trim() &&
      !!formData.snmpPort &&
      !!formData.timeout &&
      !hasStep2Errors;
      if(formData.snmpVersion === '1'){
        isEnable = isEnable &&
        !!formData.readCommunity?.trim() &&
        !!formData.writeCommunity?.trim();
      }
      else{
        const authPasswordRequired = formData.authProtocol && formData.authProtocol !== 'NO_AUTH';
        const privPasswordRequired = formData.privacyProtocol && formData.privacyProtocol !== 'NO_PRIV';
        isEnable = isEnable &&
        !!formData.username?.trim() &&
        !!formData.authProtocol?.trim() &&
        (!authPasswordRequired || !!formData.authPassword?.trim()) &&
        !!formData.privacyProtocol?.trim() &&
        (!privPasswordRequired || !!formData.privacyPassword?.trim());
      }      
    }
    else{
      isEnable = 
      !!formData.version?.trim() &&
      !!formData.modelNumber?.trim() &&
      !hasStep2Errors;
    }
  }
  
  return isEnable;
};


  const steps = isBulkUpload ? [...stepsBulkUpload] : [...step1st2nd];
  const maxSteps = steps.length;
  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleIsBulkUpload = () => {
    setIsBulkUpload(true);
    dispatch(setErrorMsg({}));
    dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, ...resetSiteOfflineDetails }));
  };
  const cancelHandler = () => {
    setIsBulkUpload(false);
    setActiveStep(0);
    dispatch(setErrorMsg({}));
    dispatch(setSiteOfflineDevice({ ...siteOfflineDetails, ...resetSiteOfflineDetails }));
    !!selectedBulkFile && dispatch(setSelectedBulkFileUpload(null));
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const submitHandler = () => {
    if (isBulkUpload && !!selectedBulkFile) {
      setIsLoadingStatus(true);
      handleBulkUpload(selectedBulkFile);
    } else if (!isBulkUpload) {
      setIsLoadingStatus(true);
      handleAddOfflineSite();
    }
    else return;
  };

  const handleBackToAddSite = () => {
    if (selectedBulkFile !== null) {
      dispatch(setSelectedBulkFileUpload(null));
    }
    setIsBulkUpload(false);
  };

  const footer = (
    <div className="flex items-center justify-between w-full">
      <div>
        {activeStep === 0 ? (
          isBulkUpload ? (
            <Button size="sm" disabled={isLoadingStatus} className="text-foreground" onClick={handleBackToAddSite}>
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
          ) : (
            <Button size="sm" disabled={isLoadingStatus} variant="outline" className="text-foreground border-border" onClick={handleIsBulkUpload}>
              Bulk upload
            </Button>
          )
        ) : (
          <Button size="sm" className="text-foreground" onClick={handleBack} disabled={activeStep === 0 || isLoadingStatus}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
        )}
      </div>

      {!isBulkUpload && maxSteps > 1 && (
        <div className="flex items-center gap-1.5">
          {Array.from({ length: maxSteps }).map((_, i) => (
            <span
              key={i}
              className={`inline-block h-2 w-2 rounded-full transition-colors ${
                i === activeStep ? "bg-primary" : "bg-border"
              }`}
            />
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" className="text-foreground" onClick={cancelHandler}>
          Cancel
        </Button>
        {activeStep === maxSteps - 1 ? (
          <Button
            size="sm"
            className="text-foreground"
            disabled={isBulkUpload
              ? (selectedBulkFile === null || Object.keys(errorMsg)?.length > 0 || isLoadingStatus)
              : (!enableNext(activeStep, siteOfflineDetails, errorMsg) || isLoadingStatus)
            }
            onClick={submitHandler}
          >
            {isLoadingStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        ) : (
          <Button
            size="sm"
            className="text-foreground"
            disabled={!enableNext(activeStep, siteOfflineDetails, errorMsg)}
            onClick={handleNext}
          >
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Sheet open={true} onClose={cancelHandler} title="Add site" footer={footer} width="520px">
      <div className="w-full py-2">
        {steps[activeStep].component}
      </div>
    </Sheet>
  );
};
export default AddSiteModal;
